import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getJobPost } from "../../api/axios";
import { doctorImageUrl } from "../../api/imageUrl";
import { timeAgo } from "../../utils/timeAgo";

function Avatar({ name, image }) {
  const src = doctorImageUrl(image);
  if (src) {
    return <img src={src} alt={name} className="w-14 h-14 rounded-full object-cover shrink-0 border border-gray-100" onError={(e) => { e.target.style.display = "none"; }} />;
  }
  return (
    <div className="w-14 h-14 rounded-full bg-[#00342b] text-white flex items-center justify-center font-bold text-lg shrink-0">
      {(name || "?").trim().charAt(0).toUpperCase()}
    </div>
  );
}

const BuildingIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1m-6 4h1m4 0h1m-6 4h1m4 0h1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PinIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const STATUS_STYLES = {
  open: "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed: "bg-gray-50 text-gray-600 border-gray-200",
  hidden: "bg-amber-50 text-amber-700 border-amber-100",
};

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setJob(null);
    setError(null);
    (async () => {
      try {
        const res = await getJobPost(id);
        if (active) setJob(res.data);
      } catch (err) {
        if (active) setError(err.message || "Job post not found");
      }
    })();
    return () => { active = false; };
  }, [id]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 text-center">
        <p className="text-gray-500 mb-4">{error}</p>
        <Link to="/jobs" className="text-[#00342b] font-semibold underline">← Back to jobs</Link>
      </div>
    );
  }

  if (!job) return <div className="p-8">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link to="/jobs" className="text-sm font-semibold text-[#00342b]">← Back to jobs</Link>

      <div className="bg-white border rounded-3xl p-6 md:p-8 mt-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLES[job.status] || STATUS_STYLES.open}`}>
              {job.status}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-100">
              <UsersIcon /> {job.vacancy_available} {Number(job.vacancy_available) === 1 ? "opening" : "openings"}
            </span>
          </div>
          <span className="text-xs text-gray-400">Posted {timeAgo(job.created_at)}</span>
        </div>

        <h1 className="text-3xl font-extrabold text-gray-900 mt-4">{job.post_name}</h1>

        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-600">
          <span className="flex items-center gap-1.5"><BuildingIcon /> {job.hospital_name}</span>
          <span className="flex items-center gap-1.5"><PinIcon /> {job.job_location}</span>
        </div>

        <div className="prose max-w-none mt-6 whitespace-pre-line text-gray-700 leading-relaxed">{job.description}</div>
      </div>

      <div className="bg-white border rounded-3xl p-6 md:p-8 mt-6">
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-4">Posted by</h2>
        <div className="flex items-start gap-4 flex-wrap">
          <Avatar name={job.author_name} image={job.author_image} />
          <div className="min-w-0 flex-1">
            <div className="font-bold text-gray-900">{job.author_name || "Member"}</div>
            <div className="text-sm text-gray-500 mt-0.5">
              {[job.author_specialty, job.graduation_degree, job.doctor_experience ? `${job.doctor_experience} yrs experience` : null].filter(Boolean).join(" · ")}
            </div>
          </div>
          <div className="flex gap-3 shrink-0">
            {job.author_phone && (
              <a href={`tel:${job.author_phone}`} className="inline-flex items-center gap-2 bg-[#00342b] text-white font-semibold text-sm py-2.5 px-5 rounded-xl hover:bg-[#004d40] transition">
                <PhoneIcon /> Call
              </a>
            )}
            {job.author_email && (
              <a href={`mailto:${job.author_email}?subject=${encodeURIComponent("Regarding: " + job.post_name)}`} className="inline-flex items-center gap-2 border border-gray-200 text-gray-700 font-semibold text-sm py-2.5 px-5 rounded-xl hover:border-gray-300 transition">
                <MailIcon /> Email
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
